import React, { useMemo } from "react";
import { Typography, TableBody, Box, useTheme } from "@mui/material";
import { format, parseJSON } from "date-fns";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import { Booking, InvestmentTypeEnum } from "@bn/api/apiv1";
import {
  toCurrency,
  getValueByInvestmentAndBookingType,
  getNumberOfLotsByInvestmentAndBookingType,
} from "@helpers";
import {
  BookingType,
  GainLossTypography,
  Note,
  TableBodyCell,
  TableRow,
} from "@components";
import { useSecurityImages, useInvestmentLink } from "@hooks";
import { LinkWithRouting } from "@components/common/navigation";

interface BookingsTableBodyProps {
  data: Booking[];
  depotId?: number;
}

export const BookingsTableBody = (props: BookingsTableBodyProps) => {
  const { data = [], depotId } = props;
  const theme = useTheme();
  const getInvestmentUrl = useInvestmentLink(depotId);

  const logoSecurity = useMemo(
    () =>
      data.map((booking) => ({
        hasValidIsin: true,
        type:
          booking.investment.quoteProvider === "currency"
            ? "81_fcurr"
            : "11_stock",
        isin: booking.investment.isin || booking.investment.tickerSymbol,
        ticker_symbol: booking.investment.tickerSymbol,
        quoteProvider: booking.investment.quoteProvider,
      })),
    [data]
  );

  // Preserve image preloading parity with desktop view.
  useSecurityImages(logoSecurity);

  return (
    <TableBody>
      {data.map((item) => {
        const instrumentUrl = getInvestmentUrl(item.investment);
        const nameContent = (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body3" color="textPrimary">
              {item.investment.name}
            </Typography>
            {item.cancellationId != undefined && (
              <CancelledIcon
                style={{
                  width: "16px",
                  height: "16px",
                  color: theme.palette.text.secondary,
                  cursor: "help",
                }}
              />
            )}
          </Box>
        );

        return (
          <TableRow key={item.id}>
            <TableBodyCell position="first">
              {instrumentUrl ? (
                <LinkWithRouting
                  href={instrumentUrl}
                  underline="hover"
                  sx={{ textAlign: "left" }}
                >
                  {nameContent}
                </LinkWithRouting>
              ) : (
                nameContent
              )}
              <Box display="flex" gap={2} alignItems="center">
                <BookingType type={item.type} />
                <Typography variant="body3" color="textSecondary">
                  {format(parseJSON(item.valueDate), "dd.MM.yyyy")}
                </Typography>
              </Box>
              <Note comment={item.comment} />
            </TableBodyCell>

            <TableBodyCell position="last" align="right">
              <GainLossTypography
                value={item.amount}
                variant="body2"
                justifyContent="end"
                fontWeight={600}
              >
                {item.amount
                  ? toCurrency(item.amount, item.investment.currency)
                  : "N/A"}
              </GainLossTypography>
              <Typography variant="body3" color="textSecondary" noWrap>
                {getNumberOfLotsByInvestmentAndBookingType({
                  value: Math.abs(item.numberOfLots),
                  investmentType: item.investment.type as InvestmentTypeEnum,
                  bookingType: item.type,
                })}{" "}
                {getValueByInvestmentAndBookingType({
                  value: Math.abs(item.securityPrice),
                  currency: item.investment.currency,
                  investmentType: item.investment.type as InvestmentTypeEnum,
                  bookingType: item.type,
                  percentageRange: "0-100",
                })}
              </Typography>
            </TableBodyCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};
